import { ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { ColDef, FilterChangedEvent, GridApi, GridOptions, RowNode, GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { FfpPlanListRecord, IUserProfile, ProjectBuildoutListRecord } from "src/app/npr-request.model";
import { ColParams } from "src/app/shared/grid/ag-grid";
import { ModalService } from "src/app/shared/modal.service";
import * as CDs from "src/app/utility/commonColDef";
import { PreprocessDateTime } from "src/app/utility/view-field-mapping";
import { ApiService } from "../../services";
import { BaseComponent } from "../../shared/base.component";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { agDateColumnFilter, cacheFilterStatsLocal, extendDefaultOptions, setupFilterByLocalData } from "../../utility/gridHelper";
import { FfpPlanSubmission } from "../../generated-models/FfpPlanSubmission";
import { ProjectBuildout } from "../../generated-models/ProjectBuildout";
import { FfpPlanInstance } from "../../generated-models/FfpPlanInstance";

interface RowDataForPlan {
  ServiceTeam: string;
  ServiceTreeId: string;
  Ring: string;
  Type: string;
  Version: number;
  CreatedTime: Date;
  CreatedBy: string;
  ProjectStatus: string;
  AssignedVersion?: number;
  AlreadyAssigned?: string;
  AssignedBy?: string;
  AssignedTime?: Date | null;
  RegionalPlanStatus?: string;
}

@Component({
  standalone: false,
  templateUrl: "./ffp-plans-assignment.component.html",
  styleUrls: ["../../styles.scss", "./ffp.scss"],
})
export class FfpPlansAssignmentComponent extends BaseComponent implements OnInit {
  projects: ProjectBuildout[] = [];
  selectedProject: ProjectBuildout;
  userProfile: IUserProfile = null;
  isAssignPslEnabled = false;

  rowDataForPlan: RowDataForPlan[] = [];

  gridOptionsForPlan: GridOptions;
  gridApi: GridApi;
  initPageSize = 15;

  ffpPlans: FfpPlanListRecord[];
  ffpPlanInstances: FfpPlanInstance[] = [];

  hideAlreadyAssigned = false;
  hasRowSelected = false;

  assignPlansToOneRegion = true;
  keyFilterStatusOneRegion = "filter:multiple-to-one";
  keyFilterStatusMultiRegions = "filter:one-to-multiple";
  canAssign = false;


  constructor(
    private apiService: ApiService,
    private modalService: ModalService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  async ngOnInit() {
    this.hasRowSelected = false;
    this.apiService
      .getUserProfile()
      .then((response) => {
        if (response) {
          this.userProfile = response;
          this.isAssignPslEnabled = this.userProfile.IsAdmin || this.userProfile.IsFfpAdmin;
        } else {
          this.notificationService.error("user profile returns null, no data was loaded.");
        }
      })
      .catch((error) => {
        this.notificationService.error(`unable to get user profile, error: ${error.message}`);
      });

    const FfpPlansColumnDefinition: ColDef[] = [
      {
        colId: "customFilter",
        valueGetter: (params: ColParams<RowDataForPlan, void>) => {
          if (this.hideAlreadyAssigned) {
            return this.isAssignable(params.data);
          }

          return true;
        },
        filterParams: {
          values: ["true", "false"],
          newRowsAction: "keep",
        },
        hide: true,
      },
      {
        ...CDs.ServiceTreeId,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
      },
      CDs.ServiceTeam,
      CDs.Ring,
      CDs.FfpPlanType,
      CDs.LatestVersion,
      CDs.CreatedBy,
      {
        headerName: "Submit Time",
        field: "CreatedTime",
        cellRenderer: (params) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => agDateColumnFilter(filterLocalDateAtMidnight, cellValue),
        },
      },
      CDs.AlreadyAssigned,
      CDs.AssignedVersion,
      CDs.AssignedBy,
      CDs.AssignedTime,
      CDs.RegionalPlanStatus,
    ];

    this.gridOptionsForPlan = extendDefaultOptions({
      columnDefs: FfpPlansColumnDefinition,
      paginationPageSize: this.initPageSize,
      isRowSelectable: (node: RowNode) => {
        return this.isAssignable(node.data);
      },
      enableRangeSelection: false,
      rowSelection: "multiple",
      animateRows: true,
    });

    this.apiService.getUserProfile().then(async (response) => {
      this.userProfile = response;
      if (response) {
        this.gridApi?.showLoadingOverlay();

        // Project Buildout List
        const ProjectBuildoutListResponse = await this.apiService.getFfpProjectBuildouts().toPromise();
        if (!ProjectBuildoutListResponse) {
          console.warn("No project obtained from server.");
        } else {
          this.projects = ProjectBuildoutListResponse
            .map((p) => ({ ...p, Label: `${p.ProjectName} (${p.ProjectOid})` } as ProjectBuildoutListRecord));
        }
      }
    });

    this.loadingService.setLoading(true);

    // Plan List
    const ffpPlanListResponse = await this.apiService.getAllFfpPlans().toPromise();
    if (!ffpPlanListResponse) {
      console.warn("No plan obtained from server.");
    } else {
      this.ffpPlans = ffpPlanListResponse
        .filter((p) => p.Version > 0)
        .map((p) => ({ ...p, Label: `${p.ServiceTeam} (${p.ServiceTreeId})` } as FfpPlanListRecord));
    }

    await this.getLatestFfpInstances();

    this.loadingService.setLoading(false);
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setFilterModel({
      customFilter: { values: ["true"] },
    });
  }

  async getLatestFfpInstances(): Promise<void> {
    return this.apiService
      .getLatestFfpInstances()
      .toPromise()
      .then(
        (response) => {
          this.ffpPlanInstances = response;
          setupFilterByLocalData(this.gridApi, this.keyFilterStatusOneRegion);
          setupFilterByLocalData(this.gridApi, this.keyFilterStatusMultiRegions);
        },
        (e: any) => {
          this.ffpPlanInstances = [];
          this.notificationService.error(e);
        }
      );
  }

  onProjectChanged(): void {
    this.rowDataForPlan = [];
    if (this.selectedProject) {
      const rowData: RowDataForPlan[] = [];

      this.ffpPlans.forEach((plan) => {
        const row: RowDataForPlan = {
          ServiceTeam: plan.ServiceTeam,
          ServiceTreeId: plan.ServiceTreeId,
          Ring: plan.Ring,
          Type: plan.Type,
          Version: plan.Version,
          ProjectStatus: this.selectedProject.Status,
          CreatedTime: plan.CreatedTime,
          CreatedBy: plan.CreatedBy
        };
        rowData.push(row);
      });

      rowData.forEach((row) => {
        row.AlreadyAssigned = "No";

        const ffpPlanInstances = this.ffpPlanInstances.find((p) => p.ProjectOid === this.selectedProject.ProjectOid && p.ServiceTreeId === row.ServiceTreeId);
        if (ffpPlanInstances) {
          if (ffpPlanInstances.Version === row.Version) {
            row.AlreadyAssigned = "Yes";
          }
          row.AssignedVersion = ffpPlanInstances.Version;
          row.AssignedBy = ffpPlanInstances.Submitter;
          row.AssignedTime = ffpPlanInstances.SubmitTime;
          row.RegionalPlanStatus = ffpPlanInstances.Status;
        }
      });
      this.rowDataForPlan = rowData;
      this.hasRowSelected = false;
      setTimeout(() => {
        this.gridApi.autoSizeAllColumns();
      }, 100);
      setupFilterByLocalData(this.gridApi, this.keyFilterStatusOneRegion);
    }
  }

  isAssignable(row: RowDataForPlan): boolean {
    return row.AlreadyAssigned === "No";
  }

  onHideAlreadyAssignedChanged(): void {
    this.gridApi.onFilterChanged();
  }

  async showFfpPlanAssignmentDialog(): Promise<void> {
    try {
      await this.assignFfpPlans("");
    } catch {
      // For the model dialog dismiss
      return;
    }
  }

  async showFfpPlanAssignmentWithPSLDialog(): Promise<void> {
    try {
      const result = await this.modalService.ffpPlanAssignmentModal();
      if (result.psl) {
        await this.assignFfpPlans(result.psl);
      }
    } catch {
      // For the model dialog dismiss
      return;
    }
  }

  async assignFfpPlans(psl: string): Promise<void> {
    let ffpPlanSubmissions: FfpPlanSubmission[];

    if (psl.length > 0) {
      var services = await this.apiService.getServiceTreeIdsByPsl(psl).toPromise();
      ffpPlanSubmissions = services.map((r) => {
        return {
          ServiceTreeId: r,
          ProjectOid: this.selectedProject.ProjectOid
        } as FfpPlanSubmission;
      })
    } else {
      const selectedRows = this.gridApi.getSelectedRows() as RowDataForPlan[];
      ffpPlanSubmissions = selectedRows.map((r) => {
        return {
          ServiceTreeId: r.ServiceTreeId,
          ServiceTeam: r.ServiceTeam,
          Version: r.Version,
          ProjectOid: this.selectedProject.ProjectOid,
        } as FfpPlanSubmission;
      });
    }

    await this.modalService.ffpPlanAssignmentResponseModal(ffpPlanSubmissions);
    await this.getLatestFfpInstances();

    // reload grid
    this.onProjectChanged();
  }

  onSelectionChanged(params: SelectionChangedEvent): void {
    var rows = params.api.getSelectedRows().length;
    this.hasRowSelected = rows > 0;
    if (!this.hasRowSelected) {
      this.canAssign = false;
    }
    else {
      this.canAssign = true;
    }
    this.cdr.detectChanges();
  }

  onAssignModeChanged(_params: any): void {
    this.hasRowSelected = false;
  }

  @HostListener("window:resize")
  onResize(): void {
    setTimeout(() => {
      this.gridApi.autoSizeAllColumns();
    }, 100);
  }

  onFilterChangedRegion(event: FilterChangedEvent): void {
    cacheFilterStatsLocal(event, this.keyFilterStatusOneRegion);
  }

  onFilterChangedPlan(event: FilterChangedEvent): void {
    cacheFilterStatsLocal(event, this.keyFilterStatusMultiRegions);
  }
}
